import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import css from './ChampionGrid.module.scss';
import { POSITIONS, POSITION_LABELS } from './championPositions';
import PositionIcon from '../europe/PositionIcon';

const SORT_OPTIONS = [
  { key: 'name', label: '字母', requiresData: false },
  { key: 'pb',   label: 'P+B', requiresData: true },
  { key: 'pr',   label: 'PR',  requiresData: true },
  { key: 'br',   label: 'BR',  requiresData: true },
  { key: 'wr',   label: '胜率', requiresData: true },
];

// 徽章显示模式：默认纯净（不显示 P/B/W）
// OP 警告徽章独立 —— 它是关键警告，由 showOP 控制（默认开）
const BADGE_STORAGE_KEY = 'lolcoach.badgeShow.v1';
const DEFAULT_BADGES = { p: false, b: false, w: false, op: true };

function loadBadgeShow() {
  try {
    const raw = localStorage.getItem(BADGE_STORAGE_KEY);
    if (!raw) return DEFAULT_BADGES;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_BADGES, ...parsed };
  } catch (e) {
    return DEFAULT_BADGES;
  }
}

const ChampionGrid = ({
  champions,
  loading,
  error,
  usedChampionIds,
  globalLockedMap, // Map<championId, { gameNumber, side, championName }>
  currentStep,
  isFinished,
  onPick,
  onUndo,
  onReset,
  tournamentData, // { lookup, isOP, winRateTier, available, data, ... }
}) => {
  const [keyword, setKeyword] = useState('');
  const [activePosition, setActivePosition] = useState('all'); // 'all' | 'top' | ...
  const [sortBy, setSortBy] = useState('name');                  // 见 SORT_OPTIONS

  // 徽章显示开关（默认纯净模式，全部关）
  const [badgeShow, setBadgeShow] = useState(loadBadgeShow);
  useEffect(() => {
    try { localStorage.setItem(BADGE_STORAGE_KEY, JSON.stringify(badgeShow)); } catch (e) {}
  }, [badgeShow]);
  const toggleBadge = (key) => setBadgeShow((s) => ({ ...s, [key]: !s[key] }));
  const setBadgePreset = (preset) => {
    if (preset === 'clean') setBadgeShow({ p: false, b: false, w: false, op: true });
    else if (preset === 'all') setBadgeShow({ p: true, b: true, w: true, op: true });
  };
  const isClean = !badgeShow.p && !badgeShow.b && !badgeShow.w;

  const td = tournamentData;
  const dataAvailable = !!(td && td.available);

  const filtered = useMemo(() => {
    let list = champions;
    if (activePosition !== 'all') {
      list = list.filter((c) =>
        Array.isArray(c.positions) && c.positions.includes(activePosition),
      );
    }
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      list = list.filter((c) => c.search.includes(kw));
    }
    // 按赛事数据排序：把没数据的统一往后排
    if (sortBy !== 'name' && dataAvailable) {
      const score = (c) => {
        const s = td.lookup(c);
        if (!s) return -Infinity;
        if (sortBy === 'pr') return s.pick_rate_pct || 0;
        if (sortBy === 'br') return s.ban_rate_pct || 0;
        if (sortBy === 'pb') return (s.pick_rate_pct || 0) + (s.ban_rate_pct || 0);
        if (sortBy === 'wr') return s.win_rate_pct || 0;
        return 0;
      };
      list = list.slice().sort((a, b) => score(b) - score(a));
    }
    return list;
  }, [keyword, activePosition, champions, sortBy, dataAvailable, td]);

  const currentLabel = (() => {
    if (isFinished) return '推演完成';
    if (!currentStep) return '';
    const teamCN = currentStep.team === 'blue' ? '蓝方' : '红方';
    const typeCN = currentStep.type === 'ban' ? 'Ban' : 'Pick';
    return `当前回合：${teamCN} · ${typeCN} #${currentStep.slot + 1}`;
  })();

  const currentSide = currentStep ? currentStep.team : null;

  return (
    <div className={cx(css.GridContainer, currentSide && css[`Side_${currentSide}`])}>
      <div className={css.Toolbar}>
        <div className={cx(css.CurrentLabel, isFinished && css.Done)}>{currentLabel}</div>
        <input
          className={css.SearchInput}
          placeholder="搜索英雄（中文 / 英文，如 Yasuo / 亚索）"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        {/* 赛事数据：排序按钮组 + 数据源徽章 */}
        <div className={css.SortGroup}>
          <span className={css.SortLabel}>排序</span>
          {SORT_OPTIONS.map((opt) => {
            const disabled = opt.requiresData && !dataAvailable;
            return (
              <button
                key={opt.key}
                type="button"
                disabled={disabled}
                className={cx(css.SortBtn, sortBy === opt.key && css.SortBtnActive)}
                onClick={() => setSortBy(opt.key)}
                title={disabled ? '需要赛事数据 (tournament_data.json)' : `按 ${opt.label} 降序`}
              >
                {opt.label}
              </button>
            );
          })}
          {dataAvailable ? (
            <span
              className={css.DataBadge}
              title={`${td.data.source.tournament || ''} · ${td.data.estimatedTotalGames} 场 · 来源 ${td.data.source.site}`}
            >
              <span className={css.DataDot} />
              {td.data.source.tournament || '赛事数据'}
              <span className={css.DataGames}>· {td.data.estimatedTotalGames}场</span>
            </span>
          ) : (
            <span className={cx(css.DataBadge, css.DataBadgeMissing)} title="未找到任何数据源，运行 data-tools/scraper.py 后会生效">
              <span className={css.DataDot} />
              赛事数据缺失
            </span>
          )}
        </div>

        {/* 徽章显示开关：默认纯净（只显示 OP 警告）。可叠加 P / B / W */}
        <div className={css.BadgeGroup}>
          <span className={css.BadgeGroupLabel}>显示</span>
          <button
            type="button"
            className={cx(css.BadgeBtn, isClean && css.BadgeBtnActive)}
            onClick={() => setBadgePreset('clean')}
            title="纯净模式：只显示 OP 警告徽章"
          >净</button>
          <button
            type="button"
            disabled={!dataAvailable}
            className={cx(css.BadgeBtn, badgeShow.p && css.BadgeBtnActive)}
            onClick={() => toggleBadge('p')}
            title="切换：英雄头像上显示出场率 P%"
          >P</button>
          <button
            type="button"
            disabled={!dataAvailable}
            className={cx(css.BadgeBtn, badgeShow.b && css.BadgeBtnActive)}
            onClick={() => toggleBadge('b')}
            title="切换：英雄头像上显示禁用率 B%"
          >B</button>
          <button
            type="button"
            disabled={!dataAvailable}
            className={cx(css.BadgeBtn, badgeShow.w && css.BadgeBtnActive)}
            onClick={() => toggleBadge('w')}
            title="切换：英雄头像上显示胜率 W% (仅高/低胜率)"
          >W</button>
          <button
            type="button"
            disabled={!dataAvailable}
            className={cx(css.BadgeBtn, !isClean && badgeShow.p && badgeShow.b && badgeShow.w && css.BadgeBtnActive)}
            onClick={() => setBadgePreset('all')}
            title="一键打开 P+B+W"
          >全</button>
          <span className={css.BadgeDivider} />
          <button
            type="button"
            disabled={!dataAvailable}
            className={cx(css.BadgeBtn, css.BadgeOPBtn, badgeShow.op && css.BadgeBtnActive)}
            onClick={() => toggleBadge('op')}
            title="切换：必 ban 级英雄的 OP 警告徽章"
          >OP</button>
        </div>
        <div className={css.Actions}>
          <button type="button" className={css.ActionBtn} onClick={onUndo}>撤销</button>
          <button type="button" className={cx(css.ActionBtn, css.Danger)} onClick={onReset}>重置 BP</button>
        </div>
      </div>

      <div className={css.PositionBar}>
        <button
          type="button"
          className={cx(css.PosBtn, activePosition === 'all' && css.PosBtnActive)}
          onClick={() => setActivePosition('all')}
        >
          <span className={css.PosCN}>全部</span>
          <span className={css.PosEN}>ALL</span>
        </button>
        {POSITIONS.map((pos) => {
          const label = POSITION_LABELS[pos];
          const isActive = activePosition === pos;
          return (
            <button
              type="button"
              key={pos}
              className={cx(css.PosBtn, css[`Pos_${pos}`], isActive && css.PosBtnActive)}
              onClick={() => setActivePosition(pos)}
            >
              <span className={css.PosIcon} aria-hidden="true">
                <PositionIcon position={pos} size={14} />
              </span>
              <span className={css.PosCN}>{label.cn}</span>
              <span className={css.PosEN}>{label.en}</span>
            </button>
          );
        })}
        <span className={css.CountHint}>{filtered.length} / {champions.length}</span>
      </div>

      {loading && <div className={css.Hint}>正在从 DataDragon 加载英雄列表…（首次需联网）</div>}
      {error && <div className={cx(css.Hint, css.HintError)}>加载失败：{error}</div>}

      {!loading && !error && (
        <div className={css.Grid}>
          {filtered.map((champ) => {
            const localUsed = usedChampionIds.has(champ.id);
            const globalLock = globalLockedMap && globalLockedMap.get(champ.id);
            const used = localUsed || !!globalLock;
            const disabled = used || isFinished;

            // 赛事数据查询
            const stat = dataAvailable ? td.lookup(champ) : null;
            const isOP = !!(stat && td.isOP(stat));
            const wrTier = stat ? td.winRateTier(stat) : null;

            // 浮窗提示文案 —— 已选/锁池信息优先；否则附带赛事数据
            let tooltipText = champ.name;
            if (globalLock) {
              const sideCN = globalLock.side === 'blue' ? 'Blue' : 'Red';
              tooltipText = `已在 Game ${globalLock.gameNumber} 被 ${sideCN} 选用`;
            } else if (localUsed) {
              tooltipText = '本局已被选 / 禁用';
            } else if (stat) {
              tooltipText =
                `${champ.name}  ·  ` +
                `P ${(stat.pick_rate_pct || 0).toFixed(1)}% · ` +
                `B ${(stat.ban_rate_pct || 0).toFixed(1)}% · ` +
                `W ${(stat.win_rate_pct || 0).toFixed(1)}%  ` +
                `(出场 ${stat.picks || 0})`;
            }

            return (
              <button
                type="button"
                key={champ.id}
                className={cx(css.Cell, {
                  [css.CellUsed]: localUsed,
                  [css.CellGlobalLock]: !!globalLock,
                  [css.CellDisabled]: disabled,
                  [css.CellOP]: isOP && !used,
                })}
                onClick={() => !disabled && onPick(champ)}
                disabled={disabled}
                data-tooltip={tooltipText}
              >
                <span className={css.CellInner}>
                  <img src={champ.squareImg} alt={champ.name} loading="lazy" />
                  <span className={css.CellName}>{champ.name}</span>

                  {/* 赛事数据徽章：按用户开关决定显示哪些（默认全关 = 纯净） */}
                  {stat && !used && !isClean && (
                    <span className={css.StatRow} aria-hidden="true">
                      {badgeShow.p && (
                        <span className={cx(css.StatPR, (stat.pick_rate_pct || 0) >= 40 && css.StatHigh)}>
                          P{Math.round(stat.pick_rate_pct || 0)}
                        </span>
                      )}
                      {badgeShow.b && (
                        <span className={cx(css.StatBR, (stat.ban_rate_pct || 0) >= 40 && css.StatHigh)}>
                          B{Math.round(stat.ban_rate_pct || 0)}
                        </span>
                      )}
                      {badgeShow.w && wrTier === 'high' && (
                        <span className={cx(css.StatWR, css.StatWRHigh)}>
                          W{Math.round(stat.win_rate_pct || 0)}
                        </span>
                      )}
                      {badgeShow.w && wrTier === 'low' && (
                        <span className={cx(css.StatWR, css.StatWRLow)}>
                          W{Math.round(stat.win_rate_pct || 0)}
                        </span>
                      )}
                    </span>
                  )}

                  {/* OP 警告徽章：核心提示，独立开关（默认开） */}
                  {isOP && !used && badgeShow.op && (
                    <span className={css.OPBadge} aria-hidden="true">OP</span>
                  )}

                  {localUsed && !globalLock && <span className={css.UsedMark}>已选</span>}
                  {globalLock && (
                    <span className={css.LockBadge} aria-hidden="true">
                      <svg viewBox="0 0 14 14" width="10" height="10">
                        <path
                          d="M4 6 L4 4.5 A3 3 0 0 1 10 4.5 L10 6 M3 6 L11 6 L11 12 L3 12 Z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.3"
                        />
                      </svg>
                      G{globalLock.gameNumber}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className={css.Hint}>未找到匹配英雄</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChampionGrid;
